import { findFilter, SearchFilter } from "./filterRegistry";

/** One comma-separated value of a `key:value` token; `from`/`to` span its raw text, quotes included. */
export interface ValuePart {
  from: number;
  to: number;
  value: string;
  quoted: boolean;
}

/**
 * One whitespace-separated chunk of the search string (quotes keep spaces in).
 * Offsets index into the raw string, `to` exclusive. Free text has `key: null`
 * and its whole span as the single value part.
 */
export interface SearchToken {
  from: number;
  to: number;
  text: string;
  /** A `-key:` token; a leading "-" on free text stays part of the text. */
  negated: boolean;
  /** Lowercased key as typed. */
  key: string | null;
  /** The key name, without the "-" and the ":". */
  keyFrom: number;
  keyTo: number;
  valueFrom: number;
  valueTo: number;
  parts: ValuePart[];
  /** Non-empty, unquoted values. */
  values: string[];
  quoted: boolean;
  /** The key names a search filter. */
  known: boolean;
  filter: SearchFilter | null;
}

const KEY_PATTERN = /^(-?)([a-z][a-z0-9_]*):/i;
/** Where the bar needs quotes: spaces or commas, or a leading quote that would open one. */
const NEEDS_QUOTES_IN_BAR = /[\s,]|^"/;
/** In a single list field spaces are fine; only commas and a leading quote need quotes. */
const NEEDS_QUOTES_IN_LIST = /,|^"/;

const isSpace = (char: string) => /\s/.test(char);

/** A quote opens only where a value starts (token start, after ":" or ","), so O"Brien stays literal. */
const opensQuote = (raw: string, i: number, valueStart: boolean) =>
  raw[i] === '"' && (valueStart || raw[i - 1] === ":" || raw[i - 1] === ",");

/** The value of a part: its surrounding quotes dropped. */
const partValue = (text: string) => (text.startsWith('"') ? text.slice(1).replace(/"$/, "") : text);

function readParts(raw: string, from: number, to: number, splitOnCommas: boolean): ValuePart[] {
  const parts: ValuePart[] = [];
  let start = from;
  let inQuotes = false;
  for (let i = from; i <= to; i++) {
    // Only whitespace before it still counts as the part's start: `Beta, "Acme, LLC"`.
    const partStart = !raw.slice(start, i).trim();
    if (i < to && (inQuotes ? raw[i] === '"' : opensQuote(raw, i, partStart))) {
      inQuotes = !inQuotes;
    }
    if (i === to || (splitOnCommas && raw[i] === "," && !inQuotes)) {
      const text = raw.slice(start, i).trim();
      parts.push({
        from: start,
        to: i,
        value: partValue(text).trim(),
        quoted: text.startsWith('"'),
      });
      start = i + 1;
    }
  }
  return parts;
}

function readToken(raw: string, from: number, to: number): SearchToken {
  const text = raw.slice(from, to);
  const keyMatch = KEY_PATTERN.exec(text);
  const filter = keyMatch ? (findFilter(keyMatch[2]) ?? null) : null;

  if (!keyMatch) {
    const parts = readParts(raw, from, to, false);
    return {
      from,
      to,
      text,
      negated: false,
      key: null,
      keyFrom: from,
      keyTo: from,
      valueFrom: from,
      valueTo: to,
      parts,
      values: parts.map((part) => part.value).filter(Boolean),
      quoted: parts[0].quoted,
      known: false,
      filter: null,
    };
  }

  const [, dash, key] = keyMatch;
  const keyFrom = from + dash.length;
  const keyTo = keyFrom + key.length;
  const parts = readParts(raw, keyTo + 1, to, filter?.multi ?? false);
  return {
    from,
    to,
    text,
    negated: dash === "-",
    key: key.toLowerCase(),
    keyFrom,
    keyTo,
    valueFrom: keyTo + 1,
    valueTo: to,
    parts,
    values: parts.map((part) => part.value).filter(Boolean),
    quoted: parts.some((part) => part.quoted),
    known: filter !== null,
    filter,
  };
}

/** Splits a search string into tokens; an unclosed quote runs to the end. */
export function tokenize(raw: string): SearchToken[] {
  const tokens: SearchToken[] = [];
  let i = 0;
  while (i < raw.length) {
    if (isSpace(raw[i])) {
      i++;
      continue;
    }
    const from = i;
    let inQuotes = false;
    while (i < raw.length && (inQuotes || !isSpace(raw[i]))) {
      if (inQuotes ? raw[i] === '"' : opensQuote(raw, i, i === from)) inQuotes = !inQuotes;
      i++;
    }
    tokens.push(readToken(raw, from, i));
  }
  return tokens;
}

/** The token the caret is in or touching the end of, if any. */
export function tokenAt(tokens: SearchToken[], caret: number): SearchToken | undefined {
  return tokens.find((token) => token.from <= caret && caret <= token.to);
}

/** A value as it must be typed: quoted (inner quotes dropped) when it holds spaces or commas. */
export function formatValue(value: string): string {
  return quoteWhere(value, NEEDS_QUOTES_IN_BAR);
}

/** Wraps the value in quotes where `needsQuotes` matches; quotes cannot be escaped, so inner ones go. */
function quoteWhere(value: string, needsQuotes: RegExp): string {
  const plain = value.trim();
  return needsQuotes.test(plain) ? `"${plain.replace(/"/g, "")}"` : plain;
}

/** Splits a typed list on unquoted commas: `Mary Ann, Smith` or `"Acme, LLC", Beta`. */
export function splitValueList(text: string): string[] {
  return readParts(text, 0, text.length, true)
    .map((part) => part.value)
    .filter(Boolean);
}

/** Values as a person types them in a single field: quoted only where a comma needs it. */
export function joinValueList(values: string[]): string {
  return values.map((value) => quoteWhere(value, NEEDS_QUOTES_IN_LIST)).join(", ");
}

export function filterTokenText(filter: SearchFilter, values: string[], negated: boolean): string {
  const dash = negated && filter.excludeParam ? "-" : "";
  return `${dash}${filter.key}:${values.map(formatValue).join(",")}`;
}

/** The current values and negation of a filter: those of its first token. */
export function readFilter(tokens: SearchToken[], filter: SearchFilter) {
  const first = tokens.find((token) => token.filter === filter);
  if (!first) return { values: [] as string[], negated: false };
  return { values: filterValues(tokens, filter, first.negated), negated: first.negated };
}

/** Every value of the filter's tokens with the given sign. */
export function filterValues(tokens: SearchToken[], filter: SearchFilter, negated: boolean) {
  return tokens
    .filter((token) => token.filter === filter && token.negated === negated)
    .flatMap((token) => token.values);
}

/**
 * Rewrites the tokens of `filter` with sign `editedSign` (default: `negated`)
 * as one token holding `values`, at the first one's place, else at the end.
 * No values removes them. Tokens of the other sign are kept.
 */
export function writeFilter(
  raw: string,
  filter: SearchFilter,
  values: string[],
  negated: boolean,
  editedSign = negated
): string {
  const replacement = values.length ? filterTokenText(filter, values, negated) : null;
  const texts: string[] = [];
  let placed = false;
  for (const token of tokenize(raw)) {
    if (token.filter !== filter || token.negated !== editedSign) {
      texts.push(token.text);
      continue;
    }
    if (!placed && replacement) texts.push(replacement);
    placed = true;
  }
  if (!placed && replacement) texts.push(replacement);
  return texts.join(" ");
}

/** Text with `[from, to)` replaced, and the caret just after the inserted text. */
export function spliceText(raw: string, from: number, to: number, text: string) {
  return { raw: raw.slice(0, from) + text + raw.slice(to), caret: from + text.length };
}
